import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { Controller, useController, useFormContext } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { getReactHookFormNestedError } from "../../../utils/errors";
import {
  END_DATE_PLACEHOLDER,
  START_DATE_PLACEHOLDER,
  BUTTON_START_NOW,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import { useEffect, useRef } from "react";
import addDays from "date-fns/addDays";
import { addMonths, differenceInDays } from "date-fns";
import { Typography } from "@mui/material";
import Button from "../../../components/Buttons/Button";

const ReportRangeDateField = ({
  control,
  name,
  errors = {},
  fullWidth = true,
  variant = "filled",
  sx = {},
  sxField = {},
  showStartNowButton = false,
}) => {
  const {
    field: { value, onChange },
  } = useController({
    control,
    name,
  });

  const refIcon = useRef(null);
  const refIcon2 = useRef(null);

  const { t } = useTranslationApp();
  const error = getReactHookFormNestedError(errors, name);
  const now = new Date();
  const { reportDateLimits = {} } = useFormContext();

  const startDateTime = value.startDateTime || null;
  const endDateTime = value.endDateTime || null;
  const maxMonthsDifference = 12;

  const getMaxAndMinStartDate = () => {
    return {
      minDate: reportDateLimits?.minStartDate || null,
      maxDate: reportDateLimits?.maxStartDate || now,
    };
  };
  const getMaxAndMinEndDate = () => {
    const maxDateByMonths = addMonths(startDateTime, maxMonthsDifference);

    const minDate = startDateTime;

    const campaignMaxEnd = reportDateLimits?.maxEndDate || now;
    const maxDate = new Date(
      Math.min(
        maxDateByMonths.getTime(),
        now.getTime(),
        new Date(campaignMaxEnd).getTime()
      )
    );

    return {
      minDate,
      maxDate,
    };
  };

  const { minDate: minStartDate, maxDate: maxStartDate } =
    getMaxAndMinStartDate();
  const { minDate: minEndDate, maxDate: maxEndDate } = getMaxAndMinEndDate();

  const onClickStartNow = (field) => {
    const now = new Date();

    // Si hay una fecha de fin y el now está después de ella, ajustar la fecha de fin
    if (endDateTime && now > endDateTime) {
      const nextWeek = addDays(now, 7);
      const isBeforeNow = nextWeek.getTime() < now.getTime();
      if (isBeforeNow) {
        field.onChange({
          ...field.value,
          startDateTime: now,
          endDateTime: nextWeek,
        });
      } else {
        const differenceDays = differenceInDays(now, now);
        field.onChange({
          ...field.value,
          startDateTime: now,
          endDateTime: addDays(now, differenceDays),
        });
      }
    } else {
      field.onChange({
        ...field.value,
        startDateTime: now,
      });
    }
  };

  const isNowValidForStartNow = () => {
    const now = new Date();

    // Si hay una fecha mínima y el now está antes de ella, no es válido
    if (minStartDate && now < minStartDate) {
      return false;
    }

    // Si hay una fecha máxima y el now está después de ella, no es válido
    if (maxStartDate && now > maxStartDate) {
      return false;
    }

    return true;
  };

  const hasDateRange = startDateTime && endDateTime;

  useEffect(() => {
    if (!hasDateRange) return;
    if ((startDateTime && !endDateTime) || (!startDateTime && endDateTime))
      return;

    if (startDateTime > endDateTime) {
      const nextWeek = addDays(startDateTime, 7);
      const isBeforeNow = nextWeek.getTime() < now.getTime();
      if (isBeforeNow) {
        onChange({ ...value, endDateTime: nextWeek });
      } else {
        const differenceDays = differenceInDays(now, startDateTime);
        onChange({
          ...value,
          endDateTime: addDays(startDateTime, differenceDays),
        });
      }
    }

    if (maxEndDate?.getTime() < endDateTime?.getTime()) {
      onChange({ ...value, endDateTime: maxEndDate });
    }
    //eslint-disable-next-line
  }, [startDateTime, endDateTime]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: {
          xs: "column",
          sm: "row",
        },
        alignItems: {
          xs: "stretch",
          sm: "center",
        },
        justifyContent: "center",
        gap: {
          xs: 2,
          sm: 2,
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          height: "100%",
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {t(START_DATE_PLACEHOLDER)}
        </Typography>
        <Controller
          name="dateRange"
          control={control}
          render={({ field }) => (
            <DatePicker
              {...field}
              onChange={(date) => {
                field.onChange({
                  ...field.value,
                  startDateTime: date,
                });
              }}
              value={startDateTime}
              errors={errors}
              fullWidth={fullWidth}
              variant={variant}
              InputAdornmentProps={{
                position: "start",
                ref: refIcon,
              }}
              {...(showStartNowButton &&
                isNowValidForStartNow() && {
                  components: {
                    ActionBar: ({ onAccept }) => (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          pb: 2,
                          px: 2,
                        }}
                      >
                        <Button
                          onClick={() => {
                            onClickStartNow(field);
                            onAccept();
                          }}
                          size="small"
                          color="secondary"
                        >
                          {t(BUTTON_START_NOW)}
                        </Button>
                      </Box>
                    ),
                  },
                })}
              renderDay={(day, _value, DayComponentProps) => {
                const isDisabled =
                  (minStartDate && day < minStartDate) ||
                  (maxStartDate && day > maxStartDate);
                return (
                  <PickersDay
                    {...DayComponentProps}
                    disabled={isDisabled}
                    sx={{ opacity: isDisabled ? 0.35 : 1 }}
                  />
                );
              }}
              renderInput={(params) => (
                <TextField
                  hiddenLabel={true}
                  {...params}
                  fullWidth={fullWidth}
                  variant={variant}
                  error={!!error}
                  onClick={() => {
                    const child = refIcon?.current?.children?.[1];
                    if (child) {
                      child?.click();
                    }
                  }}
                  sx={sxField}
                />
              )}
            />
          )}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          height: "100%",
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {t(END_DATE_PLACEHOLDER)}
        </Typography>
        <Controller
          name="dateRange"
          control={control}
          render={({ field }) => (
            <DatePicker
              {...field}
              onChange={(date) => {
                field.onChange({
                  ...field.value,
                  endDateTime: date,
                });
              }}
              value={endDateTime}
              disabled={!startDateTime}
              errors={errors}
              fullWidth={fullWidth}
              variant={variant}
              InputAdornmentProps={{
                position: "start",
                ref: refIcon2,
              }}
              renderDay={(day, _value, DayComponentProps) => {
                const isDisabled =
                  (minEndDate && day < minEndDate) ||
                  (maxEndDate && day > maxEndDate);
                return (
                  <PickersDay
                    {...DayComponentProps}
                    disabled={isDisabled}
                    sx={{ opacity: isDisabled ? 0.35 : 1 }}
                  />
                );
              }}
              renderInput={(params) => (
                <TextField
                  hiddenLabel={true}
                  {...params}
                  fullWidth={fullWidth}
                  variant={variant}
                  error={!!error}
                  onClick={() => {
                    const child = refIcon2?.current?.children?.[1];
                    if (child) {
                      child?.click();
                    }
                  }}
                  sx={sxField}
                />
              )}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default ReportRangeDateField;
